import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Flag, Mail, MessageSquare, Send, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { API } from '../App';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' },
  { value: 'spam', label: 'Spam' },
];

const STATUS_CLASS = {
  new: 'bg-blue-100 text-blue-800',
  in_review: 'bg-yellow-100 text-yellow-800',
  quoted: 'bg-purple-100 text-purple-800',
  won: 'bg-green-100 text-green-800',
  lost: 'bg-gray-100 text-gray-700',
  spam: 'bg-red-100 text-red-800',
};

function StatusBadge({ status }) {
  const cls = STATUS_CLASS[status] || 'bg-gray-100 text-gray-700';
  const label = STATUS_OPTIONS.find((s) => s.value === status)?.label || status;
  return <Badge className={cls}>{label}</Badge>;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

/**
 * Reusable Quote Manager — works for Admin and Vendor.
 * Props:
 *  - scope: 'admin' | 'vendor' (controls endpoints + features)
 *  - title: section heading
 */
export default function QuotesManager({ scope = 'admin', title = 'Quote Requests' }) {
  const isAdmin = scope === 'admin';
  const basePath = isAdmin ? `${API}/admin/quotes` : `${API}/vendors/quotes`;

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [active, setActive] = useState(null); // open quote detail
  const [response, setResponse] = useState({ message: '', price: '', send_email: true });
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(basePath, { params });
      setQuotes(res.data.quotes || []);
    } catch (e) {
      toast.error('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  }, [basePath, statusFilter]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const openQuote = async (quote_id) => {
    try {
      const res = await axios.get(`${basePath}/${quote_id}`);
      setActive(res.data);
      setResponse({ message: '', price: '', send_email: true });
      setNote('');
    } catch {
      toast.error('Could not open quote');
    }
  };

  const updateStatus = async (newStatus) => {
    if (!active) return;
    setBusy(true);
    try {
      await axios.put(`${basePath}/${active.quote_id}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      setActive({ ...active, status: newStatus });
      fetchQuotes();
    } catch {
      toast.error('Failed to update status');
    } finally {
      setBusy(false);
    }
  };

  const flagQuote = async (flagged) => {
    if (!active || !isAdmin) return;
    setBusy(true);
    try {
      await axios.put(`${basePath}/${active.quote_id}/flag`, { flagged });
      toast.success(flagged ? 'Quote flagged as suspicious' : 'Flag removed');
      setActive({ ...active, flagged });
      fetchQuotes();
    } catch {
      toast.error('Failed to flag quote');
    } finally {
      setBusy(false);
    }
  };

  const sendResponse = async () => {
    if (!active) return;
    const msg = response.message.trim();
    if (!msg) {
      toast.error('Please write a response message');
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${basePath}/${active.quote_id}/respond`, {
        message: msg,
        price: response.price.trim() || null,
        send_email: response.send_email,
      });
      const wasEmail = res.data?.response?.email_sent;
      toast.success(wasEmail ? 'Response sent and emailed to buyer' : 'Response logged');
      setResponse({ message: '', price: '', send_email: true });
      // Refresh the active quote
      const refresh = await axios.get(`${basePath}/${active.quote_id}`);
      setActive(refresh.data);
      fetchQuotes();
    } catch {
      toast.error('Failed to send response');
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!active || !isAdmin) return;
    const text = note.trim();
    if (!text) {
      toast.error('Note cannot be empty');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${basePath}/${active.quote_id}/notes`, { note: text });
      toast.success('Note added');
      setNote('');
      const refresh = await axios.get(`${basePath}/${active.quote_id}`);
      setActive(refresh.data);
    } catch {
      toast.error('Failed to add note');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid={`quotes-manager-${scope}`}>
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle data-testid="quotes-title">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {quotes.length} quote{quotes.length === 1 ? '' : 's'} {statusFilter !== 'all' ? `· filtered: ${statusFilter}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm text-muted-foreground">Filter:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="quote-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
          ) : quotes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center">
              No quote requests yet.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  {isAdmin && <TableHead>Vendor</TableHead>}
                  <TableHead>Buyer</TableHead>
                  <TableHead>Qty</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quotes.map((q) => (
                  <TableRow key={q.quote_id} data-testid={`quote-row-${q.quote_id}`}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {q.flagged && <Flag className="h-3.5 w-3.5 text-red-600" />}
                        <span>{q.product_name || '—'}</span>
                      </div>
                    </TableCell>
                    {isAdmin && <TableCell className="text-sm">{q.vendor_name || '—'}</TableCell>}
                    <TableCell className="text-sm">
                      <div>{q.buyer_name || '—'}</div>
                      <div className="text-xs text-muted-foreground">{q.buyer_email || ''}</div>
                    </TableCell>
                    <TableCell className="text-sm">{q.quantity || '—'}</TableCell>
                    <TableCell>
                      <StatusBadge status={q.status} />
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDate(q.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openQuote(q.quote_id)}
                        data-testid={`quote-open-${q.quote_id}`}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Quote: {active?.product_name}
            </DialogTitle>
          </DialogHeader>

          {active && (
            <div className="space-y-5">
              {/* Top status row */}
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={active.status} />
                {active.flagged && (
                  <Badge className="bg-red-100 text-red-800 gap-1">
                    <Flag className="h-3 w-3" /> Flagged
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground ml-auto">
                  Created {formatDate(active.created_at)}
                </span>
              </div>

              {/* Buyer + product info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Buyer</p>
                  <p className="font-medium">{active.buyer_name || '—'}</p>
                  <p className="text-xs text-muted-foreground">{active.buyer_email || '—'}</p>
                  <p className="text-xs text-muted-foreground">{active.buyer_phone || ''}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Vendor</p>
                  <p className="font-medium">{active.vendor_name || '—'}</p>
                  <p className="text-muted-foreground mt-2">Quantity</p>
                  <p>{active.quantity || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Destination</p>
                  <p>{active.destination || '—'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-muted-foreground">Buyer message</p>
                  <p className="bg-muted rounded-md p-3 text-sm whitespace-pre-wrap">
                    {active.message || '—'}
                  </p>
                </div>
              </div>

              {/* Status + flag controls */}
              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Label className="text-sm">Status:</Label>
                <Select value={active.status} onValueChange={updateStatus} disabled={busy}>
                  <SelectTrigger className="w-[160px]" data-testid="quote-detail-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Button
                    size="sm"
                    variant={active.flagged ? 'outline' : 'ghost'}
                    onClick={() => flagQuote(!active.flagged)}
                    disabled={busy}
                    data-testid="quote-flag-btn"
                  >
                    <Flag className="h-4 w-4 mr-1.5" />
                    {active.flagged ? 'Remove flag' : 'Flag as suspicious'}
                  </Button>
                )}
              </div>

              {/* Conversation history */}
              {active.responses?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-semibold mb-2">Responses sent ({active.responses.length})</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {active.responses.map((r, idx) => (
                      <div key={`resp-${idx}`} className="bg-muted/50 border rounded-md p-3 text-sm">
                        <div className="flex items-center justify-between mb-1 text-xs text-muted-foreground">
                          <span>
                            <strong>{r.by}</strong> ({r.role})
                            {r.email_sent && (
                              <span className="ml-2 text-green-700">
                                <Mail className="inline h-3 w-3 mr-0.5" /> emailed
                              </span>
                            )}
                          </span>
                          <span>{formatDate(r.at)}</span>
                        </div>
                        {r.price && <p className="text-primary font-semibold mb-1">Price: {r.price}</p>}
                        <p className="whitespace-pre-wrap">{r.message}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Send response */}
              <div className="border-t pt-4 space-y-2">
                <h4 className="text-sm font-semibold flex items-center gap-1.5">
                  <Send className="h-4 w-4" /> Send response to buyer
                </h4>
                <Input
                  placeholder="Optional price (e.g., $1,200 or Contact for terms)"
                  value={response.price}
                  onChange={(e) => setResponse({ ...response, price: e.target.value })}
                  data-testid="quote-response-price"
                />
                <Textarea
                  rows={5}
                  placeholder="Type your response to the buyer. They will receive this by email."
                  value={response.message}
                  onChange={(e) => setResponse({ ...response, message: e.target.value })}
                  data-testid="quote-response-message"
                />
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={response.send_email}
                    onChange={(e) => setResponse({ ...response, send_email: e.target.checked })}
                    data-testid="quote-response-send-email"
                  />
                  Email this response to {active.buyer_email || 'buyer'}
                </label>
                <Button
                  onClick={sendResponse}
                  disabled={busy || !active.buyer_email}
                  data-testid="quote-send-response-btn"
                >
                  {response.send_email && active.buyer_email ? (
                    <>
                      <Mail className="h-4 w-4 mr-1.5" /> Send response + email
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-1.5" /> Log response
                    </>
                  )}
                </Button>
                {!active.buyer_email && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> No buyer email on file — response will be logged only.
                  </p>
                )}
              </div>

              {/* Admin notes */}
              {isAdmin && (
                <div className="border-t pt-4 space-y-2">
                  <h4 className="text-sm font-semibold">Internal notes ({active.notes?.length || 0})</h4>
                  {active.notes?.length > 0 && (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {active.notes.map((n, idx) => (
                        <div key={`note-${idx}`} className="text-xs bg-yellow-50 border border-yellow-200 rounded-md p-2">
                          <div className="text-muted-foreground mb-0.5">
                            <strong>{n.by}</strong> · {formatDate(n.at)}
                          </div>
                          <p>{n.text}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <Textarea
                    rows={2}
                    placeholder="Internal note (not sent to buyer)"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    data-testid="quote-note-input"
                  />
                  <Button size="sm" variant="outline" onClick={addNote} disabled={busy} data-testid="quote-add-note-btn">
                    Add note
                  </Button>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setActive(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
